import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useListKnowledgeDocsQuery, useDeleteKnowledgeDocMutation, KnowledgeDocument } from '../../../api/knowledgeApi';
import { useSuperGetOrganizationsQuery } from '@/features/admin/api/superAdminApi';

export function useKnowledgeData(urlOrgId?: string) {
  const adminUserRaw = localStorage.getItem('admin_user');
  const adminUser = adminUserRaw ? JSON.parse(adminUserRaw) : null;
  const isSuperAdmin = adminUser?.role === 'SuperAdmin';

  const [orgSearch, setOrgSearch] = useState('');
  const [page, setPage] = useState(1);
  const [allOrgs, setAllOrgs] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [orgFilter, setOrgFilter] = useState(urlOrgId || '');
  const [fileTypeFilter, setFileTypeFilter] = useState<string | null>(null);
  const [sortFilter, setSortFilter] = useState('newest');

  // Fetch organizations if SuperAdmin
  const { data: orgData, isFetching: isFetchingOrgs } = useSuperGetOrganizationsQuery(
    { limit: 50, page, search: orgSearch },
    { skip: !isSuperAdmin }
  );

  useEffect(() => {
    setPage(1);
    setAllOrgs([]);
  }, [orgSearch]);

  useEffect(() => {
    if (orgData?.data?.data) {
      setAllOrgs(prev => {
        const newOrgs = [...prev];
        orgData.data.data.forEach((o: any) => {
          if (!newOrgs.find(existing => existing.id === o.id)) {
            newOrgs.push(o);
          }
        });
        return newOrgs;
      });
    }
  }, [orgData]);

  const handleScrollEnd = () => {
    if (orgData?.data?.pagination) {
      if (page < orgData.data.pagination.totalPages && !isFetchingOrgs) {
        setPage(p => p + 1);
      }
    }
  };
  
  const orgOptions = allOrgs.map((org: any) => ({
    value: org.id,
    label: org.name || org.id
  }));

  // Auto-select first org for SuperAdmin if none selected
  useEffect(() => {
    if (isSuperAdmin && orgOptions.length > 0 && !orgFilter) {
      setOrgFilter(orgOptions[0].value);
    }
  }, [isSuperAdmin, orgOptions, orgFilter]);

  // Fetch knowledge docs
  const queryParams: any = { sort: sortFilter };
  if (isSuperAdmin && orgFilter) {
    queryParams.organization_id = orgFilter;
  }
  const { data: res, isLoading, isFetching } = useListKnowledgeDocsQuery(
    queryParams,
    { skip: isSuperAdmin && !orgFilter }
  );
  
  const [deleteDocMutation] = useDeleteKnowledgeDocMutation();

  const docs: KnowledgeDocument[] = res?.data || [];
  
  const filtered = docs.filter(d => {
    const searchMatch = !search || d.title.toLowerCase().includes(search.toLowerCase()) || (d.file_name || '').toLowerCase().includes(search.toLowerCase());
    const catMatch = catFilter === 'All' || d.category === catFilter;
    let typeMatch = true;
    if (fileTypeFilter) {
      const fn = (d.file_name || '').toLowerCase();
      if (fileTypeFilter === 'pdf') typeMatch = fn.endsWith('.pdf');
      else if (fileTypeFilter === 'docx') typeMatch = fn.endsWith('.docx') || fn.endsWith('.doc');
      else if (fileTypeFilter === 'csv') typeMatch = fn.endsWith('.csv');
    }
    return searchMatch && catMatch && typeMatch;
  });

  const handleDelete = (d: KnowledgeDocument) => {
    Swal.fire({ 
      title: 'Delete Document?', 
      text: `"${d.title}" will be permanently removed.`, 
      icon: 'warning', 
      showCancelButton: true, 
      customClass: { confirmButton: 'swal-danger' },
      confirmButtonText: 'Yes, delete' 
    }).then(async r => { 
      if (r.isConfirmed) { 
        try { 
          await deleteDocMutation(d.id).unwrap(); 
        } catch { 
          Swal.fire({ title: 'Error', text: 'Delete failed.', icon: 'error' }); 
        } 
      } 
    });
  };

  const showSkeleton = isLoading || isFetching || (isSuperAdmin && !orgFilter);

  // Compute stats
  const totalStorage = docs.reduce((acc, curr) => acc + (curr.file_size || 0), 0);
  const categoriesCount = new Set(docs.map(d => d.category).filter(Boolean)).size;

  return {
    isSuperAdmin,
    orgSearch,
    setOrgSearch,
    isFetchingOrgs,
    handleScrollEnd,
    orgOptions,
    search,
    setSearch,
    catFilter,
    setCatFilter,
    fileTypeFilter,
    setFileTypeFilter,
    sortFilter,
    setSortFilter,
    orgFilter,
    setOrgFilter,
    docs,
    filtered,
    handleDelete,
    showSkeleton,
    page,
    totalStorage,
    categoriesCount
  };
}
